import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Building, Home as HomeIcon, TrendingUp, Shield, MapPin, Bed, Bath, Square, ArrowRight, Sparkles, Zap, Globe, Heart, Phone, MessageCircle } from 'lucide-react';
import FuturisticBackground from '../components/FuturisticBackground';
import LeadFormModal from '../components/LeadFormModal';

const team = [
  {
    name: 'Nazeer Awan',
    role: 'CEO',
    experience: '20+ years',
    image: '/5.jpeg',
    description: 'Visionary leader with extensive expertise in real estate development and strategic investment planning.',
    phone: '',
    whatsapp: ''
  },
  {
    name: 'Ali Raza Shah',
    role: 'Senior Property Advisor',
    experience: '15+ years',
    image: '/1.jpg',
    description: 'Leading expert in Faisal Hills and Faisal Town properties with extensive market knowledge.',
    phone: '+923335997663',
    whatsapp: 'https://wa.me/923335997663'
  },
  {
    name: 'Ali Murtaza',
    role: 'Senior Property Consultant',
    experience: '15+ years',
    image: '/2.jpeg',
    description: 'Specializes in residential properties and investment opportunities in Faisal Hills.',
    phone: '+923328563429',
    whatsapp: 'https://wa.me/923328563429'
  },
  {
    name: 'M Ismail',
    role: 'Property Advisor',
    experience: '10+ years',
    image: '/3.jpeg',
    description: 'Expert in property documentation and legal advisory for real estate transactions.',
    phone: '+923344933389',
    whatsapp: 'https://wa.me/923344933389'
  },
  {
    name: 'Yaseen Shah',
    role: 'Property Advisor',
    experience: '10+ years',
    image: '/4.png',
    description: 'Expert in residential and commercial property investments with extensive market knowledge.',
    phone: '+923343362628',
    whatsapp: 'https://wa.me/923343362628'
  }
];

const Home = () => {
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);

  const services = [
    {
      icon: Building,
      title: 'Property Sales',
      description: 'Expert guidance through the entire property buying and selling process'
    },
    {
      icon: HomeIcon,
      title: 'Property Management',
      description: 'Comprehensive management services for your real estate investments'
    },
    {
      icon: TrendingUp,
      title: 'Market Analysis',
      description: 'Detailed market insights and property valuation services'
    },
    {
      icon: Shield,
      title: 'Legal Support',
      description: 'Professional legal assistance for all property transactions'
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center justify-center overflow-hidden pt-20">
        {/* Hero Background Image */}
        <div className="absolute inset-0">
          <img 
            src="/hero bg.png" 
            alt="Hero Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Gradient overlay for consistency */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 opacity-70" />
        
        <FuturisticBackground />
        
        {/* Floating elements */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-20 left-10 w-20 h-20 bg-blue-600/20 rounded-full blur-xl animate-float"
            animate={{ scale: [1, 1.2, 1], rotate: [0, 180, 360] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <motion.div
            className="absolute top-40 right-20 w-32 h-32 bg-emerald-600/20 rounded-full blur-xl animate-float"
            animate={{ scale: [1.2, 1, 1.2], rotate: [360, 180, 0] }}
            transition={{ duration: 5, repeat: Infinity, delay: 1 }}
          />
          <motion.div
            className="absolute bottom-20 left-1/4 w-24 h-24 bg-amber-600/20 rounded-full blur-xl animate-float"
            animate={{ scale: [1, 1.3, 1], rotate: [0, 270, 540] }}
            transition={{ duration: 6, repeat: Infinity, delay: 2 }}
          />
        </div>
        
        {/* Top Center Text - Full Width Horizontal Layout */}
        <div className="absolute top-1/3 sm:top-1/2 lg:top-1/4 left-0 right-0 z-10 px-4 sm:px-6 lg:px-8 transform -translate-y-1/2">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Semi-transparent overlay for readability */}
              <div className="bg-black/40 backdrop-blur-sm rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 mx-auto max-w-6xl">
                <motion.h1 
                  className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-2 sm:mb-3 md:mb-4 bg-gradient-to-r from-white via-blue-100 to-emerald-100 bg-clip-text text-transparent leading-tight drop-shadow-lg"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.8 }}
                >
                  Tell Us Your Property Requirements
                </motion.h1>
                
                <motion.p 
                  className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl text-blue-100 leading-relaxed drop-shadow-md font-medium max-w-4xl mx-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                >
                  Users share their needs and our property experts personally find suitable options. 
                  We review your requirements and contact you directly.
                </motion.p>
              </div>
            </motion.div>
          </div>
        </div>
        
        {/* CTA Panel Container */}
        <div className="absolute bottom-6 sm:bottom-8 left-0 right-0 z-10 flex justify-center px-4">
          <motion.div 
            className="w-full max-w-xs sm:max-w-sm"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            <div className="bg-black/40 backdrop-blur-xl rounded-2xl p-3 sm:p-4 md:p-6 border border-white/10 shadow-2xl">
              {/* Primary CTA - Full Width */}
              <motion.button
                onClick={() => setIsLeadModalOpen(true)}
                className="w-full px-4 py-3 sm:px-6 sm:py-4 bg-gradient-to-r from-blue-600 to-emerald-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-emerald-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex items-center justify-center text-xs sm:text-base"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Send Your Requirements
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5" />
              </motion.button>
              
              {/* Secondary CTAs - 2 Column Grid */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-2 sm:mt-3">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Link
                    to="/faisal-hills"
                    className="block w-full px-3 py-2 sm:px-4 sm:py-3 bg-white/20 backdrop-blur-sm text-white rounded-xl font-medium hover:bg-white/30 transition-all duration-300 shadow hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center text-xs sm:text-sm border border-white/20"
                  >
                    <Building className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="hidden sm:inline">Explore Faisal Hills</span>
                    <span className="sm:hidden">Faisal Hills</span>
                  </Link>
                </motion.div>
                
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <a
                    href="tel:+923335997663"
                    className="block w-full px-3 py-2 sm:px-4 sm:py-3 bg-white/20 backdrop-blur-sm text-white rounded-xl font-medium hover:bg-white/30 transition-all duration-300 shadow hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center text-xs sm:text-sm border border-white/20"
                  >
                    <Phone className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                    Call
                  </a>
                </motion.div>
              </div>
              
              {/* Utility CTA - WhatsApp Centered */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <a
                  href="https://wa.me/923335997663"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-medium hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center text-xs sm:text-sm mt-2 sm:mt-3"
                >
                  <MessageCircle className="mr-1 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                  WhatsApp
                </a>
              </motion.div>
              
              {/* Explanatory Text */}
              <p className="text-center text-xs sm:text-sm text-blue-100 mt-3 sm:mt-4 leading-relaxed">
                Our team will review your details and contact you with suitable properties.
              </p>
              
              {/* Logo Badges */}
              <motion.div
                className="flex gap-2 sm:gap-4 justify-center items-center mt-2 sm:mt-4"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.2, duration: 0.5 }}
              >
                <motion.div
                  className="bg-white/90 backdrop-blur-md rounded-lg p-1 sm:p-2 border border-white/50"
                  whileHover={{ scale: 1.05 }}
                >
                  <img
                    src="/Faisaltown Logo-01-01.png"
                    alt="Faisal Town"
                    className="h-10 sm:h-16 w-auto object-contain"
                  />
                </motion.div>
                
                <motion.div
                  className="bg-white/90 backdrop-blur-md rounded-lg p-1 sm:p-2 border border-white/50"
                  whileHover={{ scale: 1.05 }}
                >
                  <img
                    src="/Suffa.png"
                    alt="Suffa"
                    className="h-10 sm:h-16 w-auto object-contain"
                  />
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-4 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2" />
          </div>
        </motion.div>
      </section>

      {/* Lead Generation Section */}
      <section className="py-20 bg-gradient-to-r from-neon-blue/10 to-primary-600/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-4">
              Share Your Property Requirements
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Let our property experts review your needs and find suitable options for you
            </p>
          </motion.div>

          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <motion.button
              onClick={() => setIsLeadModalOpen(true)}
              className="group relative px-8 py-4 bg-gradient-to-r from-neon-blue to-primary-600 text-white font-semibold rounded-2xl shadow-neon-blue hover:shadow-neon-blue-lg transition-all duration-300 transform hover:scale-105"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="flex items-center space-x-3">
                <Sparkles className="h-5 w-5" />
                <span>Submit Property Requirements</span>
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-r from-neon-blue to-primary-600 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10" />
            </motion.button>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <motion.div
              className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              whileHover={{ y: -5 }}
            >
              <div className="w-16 h-16 bg-neon-blue/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <HomeIcon className="h-8 w-8 text-neon-blue" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Smart Requirement Analysis</h3>
              <p className="text-gray-600">Requirements are reviewed using smart tools + expert analysis to find your perfect property match.</p>
            </motion.div>

            <motion.div
              className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              whileHover={{ y: -5 }}
            >
              <div className="w-16 h-16 bg-neon-green/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="h-8 w-8 text-neon-green" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Verified Properties</h3>
              <p className="text-gray-600">All properties are thoroughly verified and legally compliant</p>
            </motion.div>

            <motion.div
              className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              whileHover={{ y: -5 }}
            >
              <div className="w-16 h-16 bg-neon-green/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-neon-green" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Best Investment</h3>
              <p className="text-gray-600">Properties with high potential for appreciation and returns</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Meet Our Team Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-lg text-gray-600">Experienced professionals dedicated to your success</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="w-full h-64 bg-gray-100 flex items-center justify-center">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=3b82f6&color=fff&size=200`;
                    }}
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">{member.name}</h3>
                  <p className="text-blue-600 font-medium mb-2">{member.role}</p>
                  <p className="text-sm text-gray-500 mb-3">{member.experience} experience</p>
                  <p className="text-gray-600 text-sm mb-4">{member.description}</p>
                  {member.phone && (
                    <div className="flex gap-2">
                      <a
                        href={`tel:${member.phone}`}
                        className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center justify-center"
                      >
                        <Phone className="h-4 w-4 mr-1" />
                        Call
                      </a>
                      <a
                        href={member.whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 bg-green-500 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-green-600 transition-colors flex items-center justify-center"
                      >
                        <MessageCircle className="h-4 w-4 mr-1" />
                        WhatsApp
                      </a>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Faisal Hills Location Map */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-4">
              Visit Faisal Hills
            </h2>
            <p className="text-lg text-gray-600">Experience the future of sustainable living at our prime location</p>
          </motion.div>
          
          <motion.div
            className="bg-white rounded-2xl shadow-xl overflow-hidden"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="relative h-96 md:h-[500px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3320.8478428485!2d72.8427!3d33.7336!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x38dfd9b5a5b5b5b5%3A0x5b5b5b5b5b5b5b5!2sFaisal%20Hills%2C%20Taxila%2C%20Rawalpindi%2C%20Punjab%2C%20Pakistan!5e0!3m2!1sen%2s!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full h-full"
                title="Faisal Hills Location Map"
              />
              
              {/* Location Overlay */}
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg p-4 shadow-lg">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Faisal Hills</h3>
                    <p className="text-sm text-gray-600">Near Taxila, Islamabad</p>
                  </div>
                </div>
              </div>
              
              {/* Get Directions Button */}
              <div className="absolute bottom-4 right-4">
                <a
                  href="https://www.google.com/maps/dir/?api=1&destination=Faisal+Hills+Taxila+Rawalpindi+Pakistan"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-lg flex items-center space-x-2"
                >
                  <MapPin className="h-4 w-4" />
                  <span>Get Directions</span>
                </a>
              </div>
            </div>
            
            {/* Location Info Cards */}
            <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <MapPin className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Prime Location</h3>
                <p className="text-sm text-gray-600">Strategically located near Taxila with easy access to Islamabad</p>
              </div>
              <div className="text-center p-4 bg-emerald-50 rounded-xl">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Building className="h-6 w-6 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Modern Development</h3>
                <p className="text-sm text-gray-600">State-of-the-art infrastructure with world-class amenities</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-xl">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">High Investment Potential</h3>
                <p className="text-sm text-gray-600">Excellent returns with rapid development and appreciation</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            className="text-center mt-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Link
              to="/faisal-hills"
              className="inline-flex items-center text-blue-600 hover:text-blue-700 font-semibold text-lg group"
            >
              <span className="bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
                Explore Faisal Hills Project
              </span>
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600">Comprehensive real estate solutions for all your needs</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div key={index} className="text-center">
                  <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8 text-primary-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Find Your Perfect Property?</h2>
          <p className="text-xl mb-8 text-primary-100">
            Let our experienced team guide you through your real estate journey
          </p>
          <Link
            to="/contact"
            className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors inline-flex items-center justify-center"
          >
            Get Started Today
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Lead Form Modal */}
      <LeadFormModal 
        isOpen={isLeadModalOpen} 
        onClose={() => setIsLeadModalOpen(false)} 
      />
    </div>
  );
};

export default Home;
